import Vue from "vue";
import Vuex from "vuex";
import Axios from "axios";
import Shuffle from "lodash.shuffle";
import Certificate from "./certificate";
import { Notification } from "./components/Notification";
import {
  NOTIFICATION_LOADING,
  NOTIFICATION_SUCCESS,
  NOTIFICATION_FAILURE,
} from "./components/Certificate";
import { v4 as uuid } from "uuid";

Vue.use(Vuex);

const MAX_SCORE = 3;
const questionnaires = require("../data/questionnaires");

const storeModule = (questionnaire) => ({
  namespaced: true,
  state: {
    questions: [],
    answers: [],
    certificate: null,
  },
  getters: {
    possibleQuestions: ({ questions }, { questionScores }) =>
      questions.filter(({ id }) => questionScores[id] !== MAX_SCORE),
    questionScores: ({ questions, answers }) =>
      answers.reduce((scores, answer) => {
        scores[answer.question] += answer.is_correct ? 1 : 0;
        return scores;
      }, Object.fromEntries(questions.map((question) => [question.id, null]))),
    stats: ({ questions }, { questionScores }) => {
      const scores = Object.values(questionScores).reduce(
        (stats, score) => {
          stats[+score]++;
          return stats;
        },
        { 0: 0, 1: 0, 2: 0, 3: 0 }
      );
      const conditions = {
        score_0:
          scores[0] <=
          Math.round(questions.length * questionnaire.conditions.score_0),
        score_3:
          scores[3] >=
          Math.round(questions.length * questionnaire.conditions.score_3),
      };
      return {
        scores,
        conditions,
        qualified:
          scores[0] + scores[1] + scores[2] + scores[3] > 0
            ? Object.values(conditions).every((_) => _)
            : false,
      };
    },
  },
  mutations: {
    answerQuestion(state, payload) {
      state.answers.push(payload);
    },
    setAnswers(state, answers) {
      state.answers = answers;
    },
    setQuestions(state, questions) {
      state.questions = questions;
    },
    setCertificate(state, certificate) {
      state.certificate = certificate;
    },
  },
  actions: {
    async selectQuestion({ getters: { possibleQuestions, questionScores } }) {
      if (!possibleQuestions.length) {
        return false;
      }

      let question = {
        ...possibleQuestions[
          Math.floor(Math.random() * possibleQuestions.length)
        ],
      };
      question.answers = Shuffle(question.answers);
      question.score = questionScores[question.id];

      return question;
    },
    async answerQuestion({ commit, state: { questions } }, payload) {
      const [question] = questions.filter(
        (question) => question.id == payload.question
      );

      const answer = {
        is_correct:
          payload.answers.sort().join(",") ===
          question.answers
            .filter((answer) => answer.is_correct)
            .map((answer) => answer.id)
            .sort()
            .join(","),
        ...payload,
      };

      commit("answerQuestion", answer);

      return new Promise((resolve) => resolve(answer));
    },
    async generateCertificate({ commit }, data) {
      commit("setCertificate", data);

      Notification.$emit("start", { text: NOTIFICATION_LOADING });

      Axios.post(
        `/${questionnaire.identifier}/certificate`,
        new URLSearchParams({ ...data, hash: Certificate.hash(data) })
      )
        .then(() => Notification.$emit("done", { text: NOTIFICATION_SUCCESS }))
        .catch(() =>
          Notification.$emit("done", { text: NOTIFICATION_FAILURE })
        );
    },
  },
});

const initializerPlugin = async (store) => {
  let deviceId = window.localStorage.getItem("deviceId");
  if (!deviceId) {
    deviceId = uuid();
    window.localStorage.setItem("deviceId", deviceId);
  }
  store.commit("deviceId", deviceId);

  Object.keys(store._modulesNamespaceMap).forEach(async (module) => {
    const [questionnaire] = module.split("/");
    store.commit(
      `${questionnaire}/setQuestions`,
      (await Axios.get(`/data/${questionnaire}.json`)).data
    );
  });

  store.commit("setQuestionnaires", questionnaires);
};

const persistentStoragePlugin = async (store) => {
  let lastQuestionnaire;
  if ((lastQuestionnaire = window.localStorage.getItem("questionnaire"))) {
    store.commit("setQuestionnaire", lastQuestionnaire);
  }

  Object.keys(store._modulesNamespaceMap).forEach(async (module) => {
    const [questionnaire] = module.split("/");

    try {
      store.commit(
        `${questionnaire}/setAnswers`,
        JSON.parse(
          window.localStorage.getItem(
            `questionnaire/${questionnaire}/answers`
          ) || "[]"
        )
      );
    } catch (error) {
      console.error({ questionnaire, error, action: "answers" });
    }

    try {
      store.commit(
        `${questionnaire}/setCertificate`,
        JSON.parse(
          window.localStorage.getItem(
            `questionnaire/${questionnaire}/certificate`
          ) || "null"
        )
      );
    } catch (error) {
      console.error({ questionnaire, error, action: "certificate" });
    }
  });

  store.subscribe((mutation, state) => {
    const [questionnaire, type] = mutation.type.split("/");
    if (type == "answerQuestion") {
      window.localStorage.setItem(
        `questionnaire/${questionnaire}/answers`,
        JSON.stringify(state[questionnaire].answers)
      );
    }

    if (type == "setCertificate") {
      window.localStorage.setItem(
        `questionnaire/${questionnaire}/certificate`,
        JSON.stringify(state[questionnaire].certificate)
      );
    }

    if (mutation.type == "setQuestionnaire") {
      window.localStorage.setItem("questionnaire", state.questionnaire);
    }
  });
};

export function createStore() {
  return new Vuex.Store({
    strict: process.env.NODE_ENV !== "production",
    plugins: [initializerPlugin, persistentStoragePlugin],
    modules: Object.fromEntries(
      new Map(
        questionnaires.map((questionnaire) => [
          questionnaire.identifier,
          storeModule(questionnaire),
        ])
      )
    ),
    state: {
      questionnaires: [],
      questionnaire: null,
      deviceId: null,
    },
    mutations: {
      setQuestionnaires(state, questionnaires) {
        state.questionnaires = Object.fromEntries(
          new Map(
            questionnaires.map((questionnaire) => [
              questionnaire.identifier,
              questionnaire,
            ])
          )
        );
      },
      setQuestionnaire(state, questionnaire) {
        state.questionnaire = questionnaire;
      },
      deviceId(state, deviceId) {
        state.deviceId = deviceId;
      },
    },
    actions: {
      async setQuestionnaire({ commit }, questionnaire) {
        commit("setQuestionnaire", questionnaire);
      },
    },
  });
}
